import React from 'react';
import type { CountryCode } from 'libphonenumber-js';
import {
  TextField,
  InputAdornment,
} from '@mui/material';
import { isValidPhoneNumber, getCountryCallingCode, parsePhoneNumber, formatIncompletePhoneNumber, getExampleNumber, Examples } from 'libphonenumber-js';
import { useTranslate } from '@refinedev/core';
import { CountryAutocomplete } from './CountryAutocomplete';
import examples from 'libphonenumber-js/examples.mobile.json';

interface PhoneNumberInputProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  defaultCountry?: CountryCode;
  size?: 'small' | 'medium';
  name?: string;
  id?: string;
  autoFocus?: boolean;
  margin?: 'none' | 'dense' | 'normal';
}

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  label,
  error = false,
  helperText,
  required = false,
  disabled = false,
  fullWidth = true,
  placeholder,
  defaultCountry = 'FR' as CountryCode,
  size = 'medium',
  name,
  id,
  autoFocus = false,
  margin = 'normal',
}) => {
  const translate = useTranslate();
  const resolvedLabel = label ?? translate('phone.label');
  const [country, setCountry] = React.useState<CountryCode>(defaultCountry);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Get dynamic placeholder based on country using libphonenumber-js examples
  const dynamicPlaceholder = React.useMemo(() => {
    if (placeholder) return placeholder;
    try {
      const example = getExampleNumber(country, examples as unknown as Examples);
      if (example) {
        // Get national number without country code
        const nationalNumber = example.nationalNumber;
        // Format with spaces for readability
        return nationalNumber.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
      }
    } catch {
      // Fallback if example not available
    }
    return '123456789';
  }, [country, placeholder]);

  // Extract phone number without country code and format it
  const phoneNumberWithoutCountry = React.useMemo(() => {
    if (!value) return '';
    try {
      const countryCode = getCountryCallingCode(country);
      const prefix = `+${countryCode}`;
      let phoneWithoutCode = '';

      if (value.startsWith(prefix)) {
        phoneWithoutCode = value.slice(prefix.length);
      } else {
        phoneWithoutCode = value.replace(/^\+\d+/, '');
      }

      // Format the number for display
      if (phoneWithoutCode) {
        // Create a full number for formatting
        const fullNumber = `+${countryCode}${phoneWithoutCode.replace(/\s/g, '')}`;
        const formatted = formatIncompletePhoneNumber(fullNumber, country);
        // Remove the country code from formatted number
        if (formatted.startsWith(prefix)) {
          return formatted.slice(prefix.length).trim();
        }
        return phoneWithoutCode;
      }
      return '';
    } catch {
      return value;
    }
  }, [value, country]);

  // Check if phone number is complete and valid
  const checkIfComplete = React.useCallback((phoneNumber: string) => {
    if (!phoneNumber) {
      return false;
    }

    try {
      // Check if it's a valid phone number
      if (isValidPhoneNumber(phoneNumber, country)) {
        // Parse the phone number to check if it's complete
        const parsed = parsePhoneNumber(phoneNumber, country);
        if (parsed && parsed.isValid()) {
          // Auto blur the input when number is complete
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.blur();
            }
          }, 100);
          return true;
        }
      }
    } catch {
      return false;
    }
    return false;
  }, [country]);

  // Handle country change
  const handleCountryChange = (newCountry: CountryCode) => {
    setCountry(newCountry);
    if (phoneNumberWithoutCountry) {
      const countryCode = getCountryCallingCode(newCountry);
      const fullNumber = `+${countryCode}${phoneNumberWithoutCountry}`;
      onChange(fullNumber);
      // Check if the number is complete with the new country code
      checkIfComplete(fullNumber);
    }
  };

  // Handle phone input change
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (!inputValue) {
      onChange(undefined);
      return;
    }

    // Remove any non-digit characters except spaces
    const digitsOnly = inputValue.replace(/[^\d]/g, '');
    const countryCode = getCountryCallingCode(country);
    const fullNumber = `+${countryCode}${digitsOnly}`;

    // Store the unformatted number
    onChange(fullNumber);

    // Check if number is complete
    checkIfComplete(fullNumber);
  };

  // Validate phone number
  const isValid = value ? isValidPhoneNumber(value) : true;
  const showError = error || (value && !isValid) || false;
  const errorMessage = helperText || (value && !isValid ? translate('phone.invalidNumber') : '') || '';

  return (
    <TextField
      label={resolvedLabel}
      error={showError}
      helperText={errorMessage}
      required={required}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      margin={margin}
      name={name}
      id={id}
      autoFocus={autoFocus}
      value={phoneNumberWithoutCountry}
      onChange={handlePhoneChange}
      placeholder={dynamicPlaceholder}
      inputRef={inputRef}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <CountryAutocomplete
              value={country}
              onChange={handleCountryChange}
              disabled={disabled}
              size={size}
            />
          </InputAdornment>
        ),
      }}
    />
  );
};

// Export a simpler version for forms that handle their own validation
export const SimplePhoneInput: React.FC<{
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  defaultCountry?: CountryCode;
  disabled?: boolean;
  className?: string;
}> = ({
  value,
  onChange,
  placeholder,
  defaultCountry = 'FR' as CountryCode,
  disabled = false,
  className
}) => {
  return (
    <PhoneNumberInput
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      defaultCountry={defaultCountry}
      disabled={disabled}
    />
  );
};
